import { useRef, useState } from 'react';
import { api, can, getUser } from '../../lib/api';
import { day } from '../../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, Modal, useResource, useToast } from '../../components/ui';
import { RowActions } from '../../components/RowActions';
import {
  expires, plural, setBeforeConsent, type AlertRow, type CookieRow, type ProcessorRow, type PurposeRow, type View,
} from './types';

/**
 * The cookie register and its review flow.
 *
 * What the scanner guessed and what a person decided stay apart: proposals are
 * never shown as facts, and only a confirmed row reaches the banner and the
 * published policy.
 */
export default function Inventory({
  purposes, reload, onChanged, go,
}: {
  purposes: PurposeRow[];
  reload: number;
  onChanged: () => void;
  go: (v: View) => void;
}) {
  const toast = useToast();
  const writable = can(getUser(), 'cookies:edit');
  const cookies = useResource<{ cookies: CookieRow[] }>(() => api.get('/v1/cookies'), [reload]);
  const alerts = useResource<{ alerts: AlertRow[] }>(() => api.get('/v1/cookie-alerts'), [reload]);
  // Linking a cookie to a processor needs processors:view; without it the field is not offered.
  const processors = useResource<{ processors: ProcessorRow[] }>(() => api.get('/v1/processors'), []);
  const [editing, setEditing] = useState<CookieRow | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const waitingRef = useRef<HTMLDivElement>(null);

  const rows = cookies.data?.cookies ?? [];
  const proposed = rows.filter((c) => c.status === 'proposed');
  const confirmed = rows.filter((c) => c.status === 'confirmed');
  const ignored = rows.filter((c) => c.status === 'ignored');
  const active = purposes.filter((p) => p.status === 'active');
  const groups = active
    .map((p) => ({ purpose: p, cookies: confirmed.filter((c) => c.purposeId === p.id) }))
    .filter((g) => g.cookies.length);
  const essential = confirmed.filter((c) => !c.purposeId || !active.some((p) => p.id === c.purposeId));
  const processorList = processors.error ? null : processors.data?.processors ?? null;
  const processorName = (id: string | null) => (id ? processorList?.find((p) => p.id === id)?.name ?? null : null);
  const beforeConsent = rows.filter((c) => setBeforeConsent(c, purposes));

  const openAlerts = (alerts.data?.alerts ?? []).filter((a) => a.status === 'open');
  const alertCookies = new Set(openAlerts.flatMap((a) => a.cookies.filter((c) => c.status === 'proposed').map((c) => c.id)));
  const alertSince = openAlerts.reduce<string | null>((min, a) => (!min || a.createdAt < min ? a.createdAt : min), null);

  async function review(id: string, patch: Record<string, unknown>, done?: string) {
    try {
      await api.patch(`/v1/cookies/${id}`, patch);
      if (done) toast(done, 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    }
  }

  const head = (
    <thead>
      <tr>
        <th>Cookie</th>
        <th>Provider</th>
        <th>Expires</th>
        <th>Owner</th>
        <th>Purpose shown to visitors</th>
        <th />
      </tr>
    </thead>
  );

  const row = (c: CookieRow) => {
    const declare = c.status !== 'confirmed';
    const linked = processorName(c.processorId);
    return (
      <tr key={c.id}>
        <td title={[c.description, c.host, found(c)].filter(Boolean).join(' · ')}>
          <span className="mono">{c.name}</span>
          {setBeforeConsent(c, purposes) && <span className="ck-flag">Set before consent</span>}
        </td>
        <td className="small ck-cell">
          {c.vendor || <span className="muted">Not known</span>}
          {c.party === 'third' && <span className="muted"> · third party</span>}
        </td>
        <td className="small u-nowrap">{expires(c) === 'Not known' ? <span className="muted">Not known</span> : expires(c)}</td>
        <td className="small ck-cell" title={linked ? `Processor: ${linked}` : undefined}>
          {c.owner || <span className="muted">—</span>}
          {linked && <span className="muted"> · {linked}</span>}
        </td>
        <td>
          <select
            className="ck-purpose"
            value={c.purposeId ?? c.proposedPurposeId ?? ''}
            disabled={!writable}
            onChange={(e) => review(c.id, { purposeId: e.target.value || null })}
            aria-label={`Purpose for ${c.name}`}
          >
            <option value="">Essential · no consent asked</option>
            {active.map((p) => <option key={p.id} value={p.id}>{p.current.name}</option>)}
          </select>
        </td>
        <td className="u-actions">
          {writable && (
            <RowActions
              primary={declare
                ? <button className="small primary" onClick={() => review(c.id, { status: 'confirmed', purposeId: c.purposeId ?? c.proposedPurposeId ?? null }, `${c.name} declared.`)}>Declare</button>
                : <button className="small" onClick={() => setEditing(c)}>Edit</button>}
              more={[
                ...(declare ? [{ label: 'Edit', onClick: () => setEditing(c) }] : []),
                ...(c.status !== 'ignored' ? [{ label: 'Do not declare', onClick: () => review(c.id, { status: 'ignored' }, `${c.name} not declared.`) }] : []),
                ...(c.status === 'ignored' ? [{ label: 'Return to review', onClick: () => review(c.id, { status: 'proposed' }) }] : []),
              ]}
            />
          )}
        </td>
      </tr>
    );
  };

  const table = (list: CookieRow[]) => (
    <div className="table-scroll">
      <table className="data ck-table">{head}<tbody>{list.map(row)}</tbody></table>
    </div>
  );

  return (
    <>
      {openAlerts.length > 0 && (
        <div className="ck-alert" role="status">
          <span>
            {plural(openAlerts.length, 'alert')} · {plural(alertCookies.size, 'cookie')} not declared since {day(alertSince)}
          </span>
          {proposed.length > 0 && (
            <button className="ck-link" onClick={() => waitingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              Review
            </button>
          )}
        </div>
      )}

      {cookies.data && rows.length > 0 && (
        <p className="page-line">
          <strong>{confirmed.length}</strong> of {plural(rows.length, 'cookie')} declared · {proposed.length} waiting for a decision · {ignored.length} not declared
          {beforeConsent.length > 0 && <> · <span className="bad-text">{beforeConsent.length} set before consent</span></>}
        </p>
      )}

      <ErrorNote error={cookies.error} />
      {cookies.loading && !cookies.data && <Loading what="Loading cookies" />}

      {cookies.data && rows.length === 0 && (
        <Card>
          <Empty>No cookies on record.</Empty>
          {can(getUser(), 'cookies:scan') && (
            <div className="ck-empty-action"><button className="primary" onClick={() => go('scans')}>Scan website</button></div>
          )}
        </Card>
      )}

      {proposed.length > 0 && (
        <div ref={waitingRef} className="ck-anchor">
          <Card title={`Needs a decision · ${proposed.length}`}>{table(proposed)}</Card>
        </div>
      )}

      {essential.length > 0 && <Card title={`Essential · ${essential.length}`}>{table(essential)}</Card>}

      {groups.map((g) => (
        <Card key={g.purpose.id} title={`${g.purpose.current.name}${g.purpose.current.basis === 'legitimate_use' ? ' · legitimate use' : ''} · ${g.cookies.length}`}>
          {table(g.cookies)}
        </Card>
      ))}

      {cookies.data && rows.length > 0 && !proposed.length && !confirmed.length && (
        <Card><Empty>No cookies declared.</Empty></Card>
      )}

      {ignored.length > 0 && (
        <Card title={`Not declared · ${ignored.length}`}>
          <button className="small" onClick={() => setShowIgnored((v) => !v)}>{showIgnored ? 'Hide' : 'Show'}</button>
          {showIgnored && <div className="ck-gap">{table(ignored)}</div>}
        </Card>
      )}

      {editing && (
        <EditModal
          cookie={editing}
          processors={processorList}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); toast('Saved.', 'ok'); onChanged(); }}
        />
      )}
    </>
  );
}

function found(c: CookieRow): string {
  if (c.source === 'scan') return `Scan · ${plural(c.foundOn.length, 'page')}`;
  if (c.source === 'observed') return "Visitors' browsers";
  return 'Added by hand';
}

/** Cookie attributes a browser crawl reported. */
function flags(c: CookieRow): string {
  const out: string[] = [];
  if (c.httpOnly) out.push('HttpOnly');
  if (c.secure) out.push('Secure');
  if (c.sameSite) out.push(`SameSite ${c.sameSite}`);
  return out.join(' · ');
}

type ExpiryMode = 'unknown' | 'session' | 'days';

function expiryMode(c: CookieRow): ExpiryMode {
  if (c.expirySource === 'session' || c.expiryDays === 0) return 'session';
  return c.expiryDays === null ? 'unknown' : 'days';
}

function EditModal({
  cookie: c, processors, onClose, onSaved,
}: {
  cookie: CookieRow;
  processors: ProcessorRow[] | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [vendor, setVendor] = useState(c.vendor);
  const [description, setDescription] = useState(c.description);
  const [owner, setOwner] = useState(c.owner);
  const [mode, setMode] = useState<ExpiryMode>(expiryMode(c));
  const [days, setDays] = useState(c.expiryDays && c.expiryDays > 0 ? String(c.expiryDays) : '');
  const [processorId, setProcessorId] = useState(c.processorId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dayCount = Number(days);
  const daysValid = mode !== 'days' || (Number.isInteger(dayCount) && dayCount >= 1 && dayCount <= 3650);

  async function save() {
    const patch: Record<string, unknown> = {};
    if (vendor.trim() !== c.vendor) patch.vendor = vendor.trim();
    if (description.trim() !== c.description) patch.description = description.trim();
    if (owner.trim() !== c.owner) patch.owner = owner.trim();
    const nextExpiry = mode === 'unknown' ? null : mode === 'session' ? 0 : dayCount;
    const expiryChanged = mode !== expiryMode(c) || (mode === 'days' && dayCount !== c.expiryDays);
    if (expiryChanged) patch.expiryDays = nextExpiry;
    if (processors && (processorId || null) !== c.processorId) patch.processorId = processorId || null;
    if (!Object.keys(patch).length) { onClose(); return; }

    setBusy(true);
    setError(null);
    try {
      await api.patch(`/v1/cookies/${c.id}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={c.name}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !daysValid}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <div className="row">
        <Field label="Provider"><input type="text" value={vendor} maxLength={120} onChange={(e) => setVendor(e.target.value)} /></Field>
        <Field label="Owner"><input type="text" value={owner} maxLength={120} placeholder="Marketing team" onChange={(e) => setOwner(e.target.value)} /></Field>
      </div>
      <Field label="What it does">
        <textarea rows={3} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="row">
        <Field label="Expires">
          <select value={mode} onChange={(e) => setMode(e.target.value as ExpiryMode)}>
            <option value="unknown">Not known</option>
            <option value="session">Session</option>
            <option value="days">After a number of days</option>
          </select>
        </Field>
        {mode === 'days' && (
          <Field label="Days">
            <input type="number" min={1} max={3650} step={1} value={days} onChange={(e) => setDays(e.target.value)} />
          </Field>
        )}
      </div>
      {processors && (
        <Field label="Processor">
          <select value={processorId} onChange={(e) => setProcessorId(e.target.value)}>
            <option value="">None</option>
            {processors.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.status !== 'active' ? ' (disabled)' : ''}</option>
            ))}
          </select>
        </Field>
      )}
      <dl className="kv ck-kv">
        <dt>Set by</dt><dd className="mono">{c.host}</dd>
        <dt>Found</dt><dd>{found(c)}</dd>
        <dt>First seen</dt><dd>{day(c.firstSeen)}</dd>
        <dt>Last seen</dt><dd>{day(c.lastSeen)}</dd>
        {flags(c) && <><dt>Attributes</dt><dd>{flags(c)}</dd></>}
        {c.lastSeenBeforeConsent && (
          <><dt>Before consent</dt><dd>{c.setBeforeConsent ? 'Set before consent' : 'Not in the latest crawl'} · last seen {day(c.lastSeenBeforeConsent)}</dd></>
        )}
        {c.reviewedBy && <><dt>Reviewed by</dt><dd>{c.reviewedBy}</dd></>}
      </dl>
      <ErrorNote error={error} />
    </Modal>
  );
}
